import { v4 as uuidv4 } from 'uuid';

import { environment } from 'environments/environment';

import { Currency, LoginResponse, Money } from '@shared/models';

export function generateGuid() {
  return uuidv4();
}

export function getNestedField<T, R = unknown>(value: T, path: string): R | T {
  if (!value || !path) return value;
  return path.split('.').reduce((obj, key) => obj?.[key], value);
}

export type PropertyName<T> = (obj: T) => unknown;

export type ValueType = 'text' | 'number' | 'money';

function capturePath<T>(expr: PropertyName<T>): string[] {
  const path: string[] = [];
  if (!expr) {
    return path;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recorder = new Proxy<any>(
    {},
    {
      get(_target, prop) {
        if (typeof prop === 'string') {
          path.push(prop);
        }
        return recorder;
      },
      apply() {
        return recorder;
      },
    }
  );

  expr(recorder as T);
  return path;
}

export function nameof<T>(expr: PropertyName<T>): string {
  return capturePath(expr).pop()!;
}

export function nameofPath<T>(expr: PropertyName<T>, separator = '.'): string {
  return capturePath(expr).join(separator);
}

type DiffEntry<V> = { oldValue: V | undefined; newValue: V | undefined };
type DiffResult<T extends Record<string, unknown>> = {
  [K in keyof T]?: DiffEntry<T[K]>;
};

export function diffObjects<T extends Record<string, unknown>>(
  oldObj: T | null | undefined,
  newObj: T | null | undefined
): DiffResult<NonNullable<T>> {
  const oldSafe = (oldObj ?? {}) as Partial<NonNullable<T>>;
  const newSafe = (newObj ?? {}) as Partial<NonNullable<T>>;

  const result: Partial<DiffResult<NonNullable<T>>> = {};
  const keys = new Set<string>([...Object.keys(oldSafe as object), ...Object.keys(newSafe as object)]);

  for (const key of keys) {
    let oldValue = (oldSafe as unknown)[key];
    let newValue = (newSafe as unknown)[key];

    oldValue = isObject(oldValue) ? objectToString(oldValue) : oldValue;
    newValue = isObject(newValue) ? objectToString(newValue) : newValue;

    if (!Object.is(oldValue, newValue)) {
      (result as Record<string, unknown>)[key] = { oldValue, newValue };
    }
  }

  return result as DiffResult<NonNullable<T>>;
}

export function objectToString(value: unknown): unknown {
  return !isObject(value)
    ? value
    : Object.keys(value)
        .map((x) => `${x} : ${objectToString(value[x])}`)
        .join('\n');
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function toObject(value: unknown): Record<string, unknown> {
  return isObject(value) ? value : {};
}

export function parseNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function parseNumberFromText(value: unknown): number | undefined {
  if (typeof value !== 'string') {
    return parseNumber(value);
  }

  const normalizedValue = value.replace(/\s/g, '').replace(',', '.');
  if (!normalizedValue) {
    return undefined;
  }

  return parseNumber(Number(normalizedValue));
}

export function convertToValueType(value: unknown, valueType: ValueType | undefined, currency?: Currency): unknown {
  if (value === undefined || value === null || !valueType || valueType === 'text') {
    return value;
  }

  const amount = parseNumberFromText(value);
  if (amount === undefined) {
    return undefined;
  }

  return valueType === 'money' ? ({ amount, currency } as Money) : amount;
}

export function removeNullProperties<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, propertyValue]) => propertyValue !== null)) as Partial<T>;
}

export function capitalizeFirstLetter(string: string | undefined): string | undefined {
  if (string === undefined) {
    return string;
  }
  return string.charAt(0).toLocaleLowerCase() + string.slice(1);
}

export function normalizeComparisonValue(value?: string): string {
  return (value ?? '').trim().toLowerCase();
}

export function spaceBeforeCapital(value: string | undefined, firstLetterToUpperCase: boolean = false): string {
  if (value) {
    const result = value.replace(/([A-Z])/g, ' $1').trim();
    return (
      (firstLetterToUpperCase ? result.charAt(0)?.toUpperCase() : result.charAt(0)) + result.substring(1).toLowerCase()
    );
  }
  return '';
}

export function groupBy<T, K extends string | number>(array: T[], keyGetter: (item: T) => K): Record<K, T[]> {
  return array.reduce(
    (acc, item) => {
      const key = keyGetter(item);
      (acc[key] = acc[key] || []).push(item);
      return acc;
    },
    {} as Record<K, T[]>
  );
}

export function openMobileApp(loginResponse?: LoginResponse) {
  const deepLink = loginResponse
    ? `${environment.mobileAppUrl}?token=${encodeURIComponent(JSON.stringify(loginResponse))}`
    : environment.mobileAppUrl;
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.location.href = deepLink;
  }
}
